import type { JsonSchema, SchemaFormValue } from "@southfields-digital/mxmz-react-ui";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

import { getRuntimeData } from "../api/dataConnectorApi";
import { useAutoUpdateSnapshot } from "../application/autoUpdateManager";

import { useRuntimeSchema } from "./useRuntimeSchema";

type UseDataConnectorParams = {
	playlistGraphic: PlaylistGraphic;
};

type DataFetchState =
	| { status: "idle" }
	| { status: "loading-schema" }
	| { status: "ready" }
	| { status: "fetching"; data: null }
	| { status: "success"; data: unknown }
	| { status: "error"; error: string };

type State = {
	fetchState: DataFetchState;
	runtimeContext: SchemaFormValue;
	hasInitializedContext: boolean;
};

type Action =
	| { type: "SCHEMA_LOADING" }
	| { type: "SCHEMA_READY" }
	| { type: "SCHEMA_ERROR"; error: string }
	| { type: "INITIALIZE_CONTEXT"; values: SchemaFormValue }
	| { type: "FETCH_STARTED"; values: SchemaFormValue }
	| { type: "FETCH_SUCCESS"; data: unknown }
	| { type: "FETCH_ERROR"; error: string };

const reducer = (state: State, action: Action): State => {
	switch (action.type) {
		case "SCHEMA_LOADING":
			return {
				...state,
				fetchState: { status: "loading-schema" },
			};

		case "SCHEMA_READY":
			return {
				...state,
				fetchState: { status: "ready" },
			};

		case "SCHEMA_ERROR":
			return {
				...state,
				fetchState: { status: "error", error: action.error },
			};

		case "INITIALIZE_CONTEXT":
			return {
				...state,
				runtimeContext: action.values,
				hasInitializedContext: true,
				fetchState: { status: "ready" },
			};

		case "FETCH_STARTED":
			return {
				...state,
				fetchState: { status: "fetching", data: null },
				runtimeContext: action.values,
			};

		case "FETCH_SUCCESS":
			return {
				...state,
				fetchState: { status: "success", data: action.data },
			};

		case "FETCH_ERROR":
			return {
				...state,
				fetchState: { status: "error", error: action.error },
			};

		default:
			return state;
	}
};

const hasAllRequiredFields = (params: {
	schema: JsonSchema | undefined;
	context: SchemaFormValue;
}): boolean => {
	const { schema, context } = params;

	if (!schema) {
		return true;
	}

	if (!Array.isArray(schema.required) || schema.required.length === 0) {
		return true;
	}

	for (const fieldName of schema.required) {
		if (typeof fieldName !== "string") {
			continue;
		}

		const value = context[fieldName];

		if (value === undefined || value === null || value === "") {
			return false;
		}
	}

	return true;
};

type UseDataConnectorResult = {
	state: DataFetchState;
	designTimeContext: SchemaFormValue;
	mergedContext: SchemaFormValue;
	schema: ReturnType<typeof useRuntimeSchema>["schema"];
	schemaFormData: ReturnType<typeof useRuntimeSchema>["formData"];
	isLoadingSchema: boolean;
	canFetch: boolean;
	fetchData: (runtimeContext: SchemaFormValue) => void;
};

export const useDataConnector = (params: UseDataConnectorParams): UseDataConnectorResult => {
	const { playlistGraphic } = params;
	const graphic = playlistGraphic.graphic;

	const dataConnector = graphic.config.dataConnector;
	const hasDataConnector = dataConnector?.providerId !== undefined;
	const configFlowId = dataConnector?.endpoint?.configFlowId;
	const contextFromConfig = dataConnector?.context;

	const designTimeContext = useMemo<SchemaFormValue>(
		() => contextFromConfig ?? {},
		[contextFromConfig],
	);

	const [state, dispatch] = useReducer(reducer, {
		fetchState: { status: "idle" },
		runtimeContext: {},
		hasInitializedContext: false,
	});

	const abortControllerRef = useRef<AbortController | null>(null);

	const runtimeSchema = useRuntimeSchema({
		configFlowId,
		designTimeContext,
	});

	const getMergedContext = useCallback(
		(runtimeContext: SchemaFormValue) => ({
			...designTimeContext,
			...runtimeContext,
		}),
		[designTimeContext],
	);

	const canFetch = useMemo(
		() =>
			hasAllRequiredFields({
				schema: runtimeSchema.schema,
				context: getMergedContext(state.runtimeContext),
			}),
		[runtimeSchema.schema, getMergedContext, state.runtimeContext],
	);

	const autoUpdateSnapshot = useAutoUpdateSnapshot(playlistGraphic.id);

	const performFetch = useCallback(
		async (runtimeContext: SchemaFormValue) => {
			if (!hasDataConnector || !dataConnector?.endpoint) {
				dispatch({
					type: "FETCH_ERROR",
					error: "No data connector or endpoint configured",
				});
				return;
			}

			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			const controller = new AbortController();
			abortControllerRef.current = controller;

			dispatch({ type: "FETCH_STARTED", values: runtimeContext });

			try {
				const data = await getRuntimeData(
					dataConnector.endpoint,
					getMergedContext(runtimeContext),
					controller.signal,
				);

				if (controller.signal.aborted) {
					return;
				}

				dispatch({ type: "FETCH_SUCCESS", data });
			} catch (err) {
				if (controller.signal.aborted || (err instanceof Error && err.name === "AbortError")) {
					return;
				}
				const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
				dispatch({ type: "FETCH_ERROR", error: errorMessage });
			} finally {
				if (abortControllerRef.current === controller) {
					abortControllerRef.current = null;
				}
			}
		},
		[hasDataConnector, dataConnector, getMergedContext],
	);

	const fetchData = useCallback(
		(runtimeContext: SchemaFormValue) => {
			if (!canFetch) {
				return;
			}
			void performFetch(runtimeContext);
		},
		[canFetch, performFetch],
	);

	// Handle schema loading states and initialize context with formData
	useEffect(() => {
		if (!hasDataConnector) {
			return;
		}

		if (runtimeSchema.isLoading) {
			dispatch({ type: "SCHEMA_LOADING" });
			return;
		}

		if (runtimeSchema.error) {
			dispatch({ type: "SCHEMA_ERROR", error: runtimeSchema.error });
			return;
		}

		// Schema loaded successfully - initialize context with formData if not already done
		if (!state.hasInitializedContext && runtimeSchema.schema) {
			const initialValues: SchemaFormValue = {
				...designTimeContext,
				...runtimeSchema.formData,
			};
			dispatch({ type: "INITIALIZE_CONTEXT", values: initialValues });
			return;
		}

		// Schema loaded but already initialized - just mark as ready if still idle
		if (state.fetchState.status === "idle" || state.fetchState.status === "loading-schema") {
			dispatch({ type: "SCHEMA_READY" });
		}
	}, [
		hasDataConnector,
		runtimeSchema.isLoading,
		runtimeSchema.error,
		runtimeSchema.schema,
		runtimeSchema.formData,
		designTimeContext,
		state.hasInitializedContext,
		state.fetchState.status,
	]);

	useEffect(() => {
		if (autoUpdateSnapshot.lastData === undefined) {
			return;
		}

		dispatch({ type: "FETCH_SUCCESS", data: autoUpdateSnapshot.lastData });
	}, [autoUpdateSnapshot.lastData]);

	useEffect(() => {
		if (!autoUpdateSnapshot.error) {
			return;
		}

		dispatch({ type: "FETCH_ERROR", error: autoUpdateSnapshot.error });
	}, [autoUpdateSnapshot.error]);

	// Cleanup on unmount
	useEffect(() => () => abortControllerRef.current?.abort(), []);

	return {
		state: state.fetchState,
		designTimeContext,
		mergedContext: getMergedContext(state.runtimeContext),
		schema: runtimeSchema.schema,
		schemaFormData: runtimeSchema.formData,
		isLoadingSchema: runtimeSchema.isLoading,
		canFetch,
		fetchData,
	};
};
